import React, { useMemo } from "react";
import { Table, Tag } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ceil, round, sum } from "lodash";
import { COLORS, UI_DATE_FORMAT } from "../../../../consts";
import { cmToInches, inchesToCm } from "../../../../utils/cmToInchesConversion";

// function getColorForPercentage(value) {
//   const pct = Math.max(0, Math.min(100, value));
//   const hue = 120 - (120 * pct) / 100;
//   return `hsl(${hue}, 100%, 50%)`;
// }

function getColorForPercentage(value, invert = false) {
  const pct = Math.max(0, Math.min(100, value));
  const hue = invert
    ? (120 * pct) / 100 // flipped: 0 % red, 100 % green
    : 120 - (120 * pct) / 100; // normal : 0 % green, 100 % red
  return `hsl(${hue}, 100%, 50%)`;
}

const ForecastSummaryTable = ({
  tableData,
  totalActualHeadCount,
  specs = [],
  minimumSizeToStart,
  tableTitle = "Forecast Summary Table",
  isImperial = false,
}) => {
  const isTotalHeadCountMissing =
    !Number.isFinite(Number(totalActualHeadCount)) ||
    Number(totalActualHeadCount) <= 0;

  /**
   * STEP 1) Gather all forecast rows unconditionally (no early return).
   */
  const allRows = useMemo(() => {
    if (!tableData) return [];
    return tableData;
  }, [tableData]);

  /**
   * 2) Build columns & row data from the forecast rows
   */
  const { columns, data } = useMemo(() => {
    // If no rows or no specs => return empty columns & data
    if (!allRows.length || !specs.length) {
      return { columns: [], data: [] };
    }


    // Helper for display: convert cm to inches for imperial, otherwise display as-is
    const displayBound = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) return "";
      // For imperial, convert cm back to inches for display
      const displayVal = isImperial ? cmToInches(num) : num;
      return Number.isInteger(displayVal) ? String(Math.round(displayVal)) : displayVal.toFixed(2);
    };
    const unit = isImperial ? "in" : "cm";

    // --------------------------------------------------------------------
    // A) Compute final lower/upper for each spec from the *first* row's data
    // --------------------------------------------------------------------
    const firstRowData = allRows[0];
    let specsWithBounds = specs.map((spec) => {
      const lbRaw = parseFloat(firstRowData[spec.lower_bound_key_name]) || 0;
      const ubRaw = parseFloat(firstRowData[spec.upper_bound_key_name]) || 0;

      // Convert bounds to cm for filtering
      // Imperial: bounds are in inches → cm (×2.54)
      // Metric: already in cm
      const lbScaled = isImperial ? inchesToCm(lbRaw) : lbRaw;
      const ubScaled = isImperial ? inchesToCm(ubRaw) : ubRaw;

      let finalLower;
      if (spec.lower_buffer_logic === "sub") {
        finalLower = lbScaled - (spec[spec.buffer_lower_bound_key_name] || 0);
      } else {
        finalLower = lbScaled + (spec[spec.buffer_lower_bound_key_name] || 0);
      }

      let finalUpper;
      if (spec.upper_buffer_logic === "sub") {
        finalUpper = ubScaled - (spec[spec.buffer_upper_bound_key_name] || 0);
      } else {
        finalUpper = ubScaled + (spec[spec.buffer_upper_bound_key_name] || 0);
      }

      const lower = Math.min(finalLower, finalUpper);
      const upper = Math.max(finalLower, finalUpper);

      return {
        ...spec,
        displayLower: Math.min(lbScaled, ubScaled),
        displayUpper: Math.max(lbScaled, ubScaled),
        finalLower: lower,
        finalUpper: upper,
      };
    });

    // Sort specs by finalLower
    specsWithBounds.sort((a, b) => a.finalLower - b.finalLower);

    // Overall "UnderSpec" starts at minimumSizeToStart or the first spec's lower,
    // Overall "OverSpec" ends at last spec's upper
    const lowestLowerBound =
      specsWithBounds[0]?.finalLower || minimumSizeToStart;
    const highestUpperBound =
      specsWithBounds[specsWithBounds.length - 1]?.finalUpper || 9999;

    // --------------------------------------------------------------------
    // B) Build the column definitions
    // --------------------------------------------------------------------

    // 1) Heads columns (UnderSpec + each spec + OverSpec)
    const headsCols = [];
    if (lowestLowerBound > minimumSizeToStart) {
      headsCols.push({
        title: `Heads Under Spec (${displayBound(minimumSizeToStart)}${unit} - ${
          displayBound(specsWithBounds[0]?.displayLower)
        }${unit})`,
        dataIndex: "heads_UnderSpec",
        key: "heads_UnderSpec",
        width: 150,
      });
    } else {
      headsCols.push({
        title: `Heads Under Spec (<${displayBound(minimumSizeToStart)}${unit})`,
        dataIndex: "heads_UnderSpec",
        key: "heads_UnderSpec",
        width: 150,
      });
    }
    specsWithBounds.forEach((sp) => {
      headsCols.push({
        title: `Heads in ${sp.label} (${displayBound(sp.displayLower)}${unit} - ${displayBound(sp.displayUpper)}${unit})`,
        dataIndex: `heads_${sp.label}`,
        key: `heads_${sp.label}`,
        width: 150,
      });
    });
    headsCols.push({
      title: `Heads Over Spec (${displayBound(
        specsWithBounds[specsWithBounds.length - 1]?.displayUpper
      )}+ ${unit})`,
      dataIndex: "heads_OverSpec",
      key: "heads_OverSpec",
      width: 150,
    });

    // 2) Percentage columns
    const pctCols = [
      {
        title: "Percentage of Heads Under Spec",
        dataIndex: "pct_UnderSpec",
        key: "pct_UnderSpec",
        width: 110,
        render: (val) => {
          const color = getColorForPercentage(val);
          return (
            <div
              style={{
                backgroundColor: color,
                color: "#000",
                padding: "2px 6px",
                borderRadius: "4px",
                textAlign: "right",
                minWidth: "50px",
              }}>
              {val}%
            </div>
          );
        },
      },
    ];
    specsWithBounds.forEach((sp) => {
      pctCols.push({
        title: `Percentage of Heads in ${sp.label} Spec`,
        dataIndex: `pct_${sp.label}`,
        key: `pct_${sp.label}`,
        width: 100,
        render: (val) => {
          const color = getColorForPercentage(val, true);
          return (
            <div
              style={{
                backgroundColor: color,
                color: "#000",
                padding: "2px 6px",
                borderRadius: "4px",
                textAlign: "right",
                minWidth: "50px",
              }}>
              {val}%
            </div>
          );
        },
      });
    });
    pctCols.push({
      title: "Percentage of Heads Over Spec",
      dataIndex: "pct_OverSpec",
      key: "pct_OverSpec",
      width: 110,
      render: (val) => {
        const color = getColorForPercentage(val);
        return (
          <div
            style={{
              backgroundColor: color,
              color: "#000",
              padding: "2px 6px",
              borderRadius: "4px",
              textAlign: "right",
              minWidth: "50px",
            }}>
            {val}%
          </div>
        );
      },
    });

    // 3) Yield columns (per spec)
    const yieldCols = specsWithBounds.map((sp) => ({
      title: `Yield (${sp.label})`,
      dataIndex: `yield_${sp.label}`,
      key: `yield_${sp.label}`,
      width: 120,
      render: (val, record) => {
        const pctVal = record[`pct_${sp.label}`] || 0;
        const color = getColorForPercentage(pctVal, true);
        return (
          <div
            style={{
              backgroundColor: color,
              color: "#000",
              padding: "2px 6px",
              borderRadius: "4px",
              textAlign: "right",
            }}>
            {val}
          </div>
        );
      },
    }));

    // 4) Harvested Yield columns (per spec)
    //    We'll compute them from rowData.harvested_sizes if row is harvested.
    const harvestedYieldCols = specsWithBounds.map((sp) => ({
      title: `Harvested Yield (${sp.label})`,
      dataIndex: `harvested_yield_${sp.label}`,
      key: `harvested_yield_${sp.label}`,
      align: "center",
      width: 140,
      render: (val) => val || 0,
    }));

    // The final set of columns
    const finalColumns = [
      {
        title: "Forecast Day",
        dataIndex: "forecastDay",
        key: "forecastDay",
        fixed: "left",
        width: 100,
      },
      {
        title: "Forecast Date",
        dataIndex: "forecastDate",
        key: "forecastDate",
        fixed: "left",
        width: 130,
      },
      ...headsCols,
      ...pctCols,
      ...yieldCols,
      ...harvestedYieldCols,
    ];

    // --------------------------------------------------------------------
    // C) Build the row data
    // --------------------------------------------------------------------
    const summaryRows = allRows.map((rowData, idx) => {
      const dateStr = rowData.forecast_date;
      const sizes = rowData.sizes || [];
      const harvestedSizes = rowData.harvested_sizes || [];
      const rowCount = sizes.length || 1;

      // 1) Normal catCounts from `sizes`
      const catCounts = {};

      // UnderSpec
      if (lowestLowerBound > minimumSizeToStart) {
        catCounts["UnderSpec"] = sizes.filter(
          (sz) => sz >= minimumSizeToStart && sz < lowestLowerBound,
        ).length;
      } else {
        catCounts["UnderSpec"] = 0;
      }

      // Each spec
      specsWithBounds.forEach((sp) => {
        catCounts[sp.label] = sizes.filter(
          (sz) => sz >= sp.finalLower && sz < sp.finalUpper,
        ).length;
      });

      // OverSpec
      catCounts["OverSpec"] = sizes.filter(
        (sz) => sz >= highestUpperBound,
      ).length;

      // 2) Scale normal counts
      const scaledCounts = {};
      Object.entries(catCounts).forEach(([catKey, rawVal]) => {
        const scaledVal = (rawVal / rowCount) * (totalActualHeadCount || 0);
        scaledCounts[catKey] = Math.round(scaledVal);
      });
      const totalScaledHeads = sum(Object.values(scaledCounts));

      // 3) Percentages
      const pctVals = {};
      Object.entries(scaledCounts).forEach(([catKey, scVal]) => {
        pctVals[catKey] =
          totalScaledHeads > 0 ? round((scVal / totalScaledHeads) * 100) : 0;
      });

      // 4) Normal yields (per spec) from scaled normal counts
      const yieldVals = {};
      specsWithBounds.forEach((sp) => {
        const headsSpec = scaledCounts[sp.label] || 0;
        yieldVals[sp.label] = ceil(headsSpec / (sp.divide_by_number || 1));
      });

      // 5) Build row object
      const rowObj = {
        key: rowData.key || `row-${idx}`,
        forecastDay: dayjs(dateStr).format("ddd").toUpperCase(),
        forecastDate: dayjs(dateStr).format(UI_DATE_FORMAT),
        heads_UnderSpec: scaledCounts["UnderSpec"] || 0,
        pct_UnderSpec: pctVals["UnderSpec"] || 0,
        heads_OverSpec: scaledCounts["OverSpec"] || 0,
        pct_OverSpec: pctVals["OverSpec"] || 0,
      };

      // Fill heads_X, pct_X, yield_X
      specsWithBounds.forEach((sp) => {
        rowObj[`heads_${sp.label}`] = scaledCounts[sp.label] || 0;
        rowObj[`pct_${sp.label}`] = pctVals[sp.label] || 0;
        rowObj[`yield_${sp.label}`] = yieldVals[sp.label] || 0;
      });

      // --------------------------------------------------------------------
      // 6) Compute "Harvested Yield" from harvested_sizes if row is harvested
      // --------------------------------------------------------------------
      if (rowData.harvested_date) {
        // A) We need to compute the catCounts from harvested_sizes
        //    (similar to how we computed from `sizes`)
        const harvCountObj = {};

        // UnderSpec (only if relevant to your logic)
        if (lowestLowerBound > minimumSizeToStart) {
          harvCountObj["UnderSpec"] = harvestedSizes.filter(
            (sz) => sz >= minimumSizeToStart && sz < lowestLowerBound,
          ).length;
        } else {
          harvCountObj["UnderSpec"] = 0;
        }

        // For each spec
        specsWithBounds.forEach((sp) => {
          harvCountObj[sp.label] = harvestedSizes.filter(
            (sz) => sz >= sp.finalLower && sz < sp.finalUpper,
          ).length;
        });

        // OverSpec
        harvCountObj["OverSpec"] = harvestedSizes.filter(
          (sz) => sz >= highestUpperBound,
        ).length;

        // B) Scale them
        const rowCountHarv = harvestedSizes.length || 1;
        const scaledHarvCounts = {};
        Object.entries(harvCountObj).forEach(([catKey, rawVal]) => {
          const scaledVal =
            (rawVal / rowCountHarv) * (totalActualHeadCount || 0);
          scaledHarvCounts[catKey] = Math.round(scaledVal);
        });

        // C) Finally, compute yield from scaledHarvCounts
        specsWithBounds.forEach((sp) => {
          const headsSpecHarv = scaledHarvCounts[sp.label] || 0;
          const baseYield = headsSpecHarv / (sp.divide_by_number || 1);
          rowObj[`harvested_yield_${sp.label}`] = ceil(
            baseYield * (1 - (rowData.quality_reduction_factor || 0)),
          );
        });
      } else {
        // Not harvested => set 0
        specsWithBounds.forEach((sp) => {
          rowObj[`harvested_yield_${sp.label}`] = 0;
        });
      }

      return rowObj;
    });

    return { columns: finalColumns, data: summaryRows };
  }, [allRows, specs, totalActualHeadCount, minimumSizeToStart, isImperial]);

  // ------------------------------------------------------------------------
  // 3) Render: if columns/data are empty => show placeholder, else show table
  // ------------------------------------------------------------------------
  if (!columns.length || !data.length) {
    return (
      <div className="my-4 rounded-md p-2.5 shadow-md transition-shadow duration-300 hover:shadow-2xl transform hover:scale-100 md:p-2 sm:p-1.5">
        <h2 className="font-bold text-lg mb-2">{tableTitle}</h2>
        <p>No data or specs to display.</p>
      </div>
    );
  }

  return (
    <div className="my-4 rounded-md p-2.5 shadow-md transition-shadow duration-300 hover:shadow-2xl transform hover:scale-100 md:p-2 sm:p-1.5">
      <h2 className="font-bold text-lg mb-4">{tableTitle}</h2>
      {isTotalHeadCountMissing && (
        <Tag
          icon={<ExclamationCircleOutlined />}
          style={{ color: COLORS.primaryBlue }}
          className="mb-3 mr-0 inline-flex items-center gap-1 bg-transparent px-2 py-0.5 text-xs font-semibold text-primaryBlue">
          Missing total head count
        </Tag>
      )}
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ x: 1400 }}
      />
    </div>
  );
};

export default ForecastSummaryTable;
